import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

import { ISuggestion, Key } from './../type-ahead.models';

@Component({
  selector: 'ns-type-ahead',
  templateUrl: 'type-ahead.component.html',
  styleUrls: ['type-ahead.component.scss']
})
export class TypeAheadComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('input') input: ElementRef;

  @Input()
  searchFn: (searchTerm$: Observable<string>) => Observable<ISuggestion<any>[]>;
  @Input() searchControl: FormControl;
  @Input() label: string;
  @Input() className = 'textInput textInput--fullWidth';
  @Input() minSearchLength = 2;
  @Input() suggestIcon = '';
  @Input() placeholder = '';
  @Input() autoFocus: boolean;
  @Input() privacySensitive: boolean;
  @Input() autocomplete = false;

  @Output() suggestionSelected = new EventEmitter<ISuggestion<any>>();
  @Output() focusout = new EventEmitter<FocusEvent>();
  @Output() focus = new EventEmitter<FocusEvent>();

  subscriptions = new Subscription();
  suggestions: ISuggestion<any>[] = [];
  showSuggestions: boolean;

  ngOnInit(): void {
    const { searchControl, minSearchLength, subscriptions } = this;
    if (!searchControl) return;
    searchControl.reset(searchControl.value);

    subscriptions.add(
      this.searchFn(
        searchControl.valueChanges.pipe(
          map((value: string) => (value ? value.trim() : value)),
          tap((value: string) => {
            if (!value || value.length < minSearchLength) {
              this.showSuggestions = false;
              this.suggestions = [];
            }
          }),
          filter((value: string) => value != null && value.length >= minSearchLength)
        )
      )
        .pipe(
          filter(suggestions => {
            return (this.showSuggestions = suggestions.length && searchControl.value);
          })
        )
        .subscribe(suggestions => {
          this.suggestions = suggestions.map(suggestion => {
            suggestion.searchText = searchControl.value;
            return suggestion;
          });
        })
    );
  }

  ngAfterViewInit(): void {
    const { autoFocus, input, searchControl } = this;
    setTimeout(() => {
      if (autoFocus && !searchControl.valid) {
        input.nativeElement.focus();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  highlightNext(suggestions: ISuggestion<any>[]): void {
    if (suggestions.length === 0) return;

    const hasAnyHighlighted = suggestions.some(suggestion => suggestion.highlighted);
    if (!hasAnyHighlighted) {
      suggestions[0].highlighted = true;
    } else {
      const currentHighlightedIndex = suggestions.findIndex(suggestion => suggestion.highlighted);
      suggestions[currentHighlightedIndex].highlighted = false;
      suggestions[(currentHighlightedIndex + 1) % suggestions.length].highlighted = true;
    }
  }

  highlightPrevious(suggestions: ISuggestion<any>[]): void {
    if (suggestions.length === 0) return;

    const hasAnyHighlighted = suggestions.some(suggestion => suggestion.highlighted);
    if (!hasAnyHighlighted) {
      suggestions[suggestions.length - 1].highlighted = true;
    } else {
      const currentHighlightedIndex = suggestions.findIndex(suggestion => suggestion.highlighted);
      suggestions[currentHighlightedIndex].highlighted = false;
      suggestions[
        (currentHighlightedIndex === 0 ? suggestions.length : currentHighlightedIndex) - 1
      ].highlighted = true;
    }
  }

  setSearchControlValue(item: ISuggestion<any>): void {
    this.showSuggestions = false;
    this.suggestions = [];

    if (!item) return;
    const { suggestionSelected, searchControl } = this;
    searchControl.setValue(item.displayText, { emitEvent: false });
    suggestionSelected.emit(item);
  }

  onBlur(): void {
    const { suggestions, showSuggestions } = this;
    this.showSuggestions = suggestions.some(suggestion => suggestion.highlighted);
    if (!showSuggestions) this.suggestions = [];
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const { keyCode } = event;
    const { suggestions, highlightNext, highlightPrevious } = this;

    switch (keyCode) {
      case Key.HOME:
        this.setSearchControlValue([...suggestions].shift());
        break;
      case Key.END:
        this.setSearchControlValue([...suggestions].pop());
        break;
      case Key.ENTER:
        event.preventDefault();
        this.setSearchControlValue(suggestions.find(({ highlighted }) => highlighted));
        break;
      case Key.ARROW_DOWN:
        event.preventDefault();
        highlightNext(suggestions);
        break;
      case Key.ARROW_UP:
        event.preventDefault();
        highlightPrevious(suggestions);
        break;
      case Key.ESCAPE:
        event.preventDefault();
        this.showSuggestions = false;
        this.suggestions = [];
        break;
    }
  }
}
